import { useApolloClient, useMutation } from "@apollo/client";
import { PrimaryFullScreenSpinner, toast, useAppForm } from "@repo/ui";
import { dateSchema, decimalSchema, prettyPrintEnum } from "@repo/utils";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Suspense } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { demoTransactionPatternValues } from "~/gql/client/enumValues";
import { opsHead } from "~/head";

const formSchema = z.object({
  currentBalance: decimalSchema.refine((v) => v.gt(0), {
    message: "Must be greater than 0",
  }),
  pattern: z.enum(demoTransactionPatternValues),
  firstTransactionDate: dateSchema,
  transactionCountPerWeek: z.coerce.number().positive().optional().default(1),
});

type FormValues = z.input<typeof formSchema>;

export const Route = createFileRoute(
  "/__auth/assets_/$assetId_/create-demo-transactions",
)({
  head: () => opsHead("Create Demo Transactions"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { assetId } = useParams({
    from: "/__auth/assets_/$assetId_/create-demo-transactions",
  });
  const client = useApolloClient();
  const navigate = useNavigate();

  const [createDemoTransactions] = useMutation(
    graphql(`
      mutation AdminCreateDemoTransactions(
        $input: CreateDemoTransactionsInput!
      ) {
        createDemoTransactions(input: $input) {
          asset {
            id
          }
        }
      }
    `),
    {
      onError(error) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating demo transactions",
          description:
            error.graphQLErrors?.[0]?.message ??
            "Failed to create transactions",
        });
      },
      onCompleted: (result) => {
        client.cache.evict({
          id: `Asset:${result.createDemoTransactions.asset.id}`,
        });
        void navigate({
          to: "/assets/$assetId",
          params: { assetId },
        });
      },
    },
  );

  const form = useAppForm({
    defaultValues: {
      currentBalance: new Decimal(0),
      pattern: "FLAT",
      firstTransactionDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
      transactionCountPerWeek: 1,
    } as unknown as FormValues,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (!parsed.success) {
        toast({ kind: "error", autoHide: false, title: "Error in form" });
        return;
      }
      void createDemoTransactions({
        variables: {
          input: {
            assetId,
            currentBalance: parsed.data.currentBalance,
            pattern: parsed.data.pattern,
            firstTransactionDate: parsed.data.firstTransactionDate,
            transactionCountPerWeek: parsed.data.transactionCountPerWeek,
          },
        },
      });
    },
  });

  return (
    <AdminPageContainer testId="admin-create-demo-transactions">
      <AdminPageHeader
        title="Create Demo Transactions"
        subtitle={`Generate realistic-looking transaction history for asset ${assetId}`}
      />
      <form.AppForm>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
          className="flex max-w-sm flex-col gap-4"
        >
          <form.AppField name="currentBalance">
            {(field) => (
              <field.UnitAmountField
                label="Current Balance"
                placeholder="0.00"
                align="start"
              />
            )}
          </form.AppField>

          <form.AppField name="pattern">
            {(field) => (
              <field.SelectField
                label="Pattern"
                placeholder="Select pattern..."
              >
                {demoTransactionPatternValues.map((p) => (
                  <field.SelectItem key={p} value={p}>
                    {prettyPrintEnum(p)}
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>

          <form.AppField name="firstTransactionDate">
            {(field) => (
              <field.DatePickerField label="First Transaction Date" />
            )}
          </form.AppField>

          <form.AppField name="transactionCountPerWeek">
            {(field) => (
              <field.UnitAmountField
                label="Transactions Per Week (optional, default 1)"
                placeholder="1"
                align="start"
                decimalScale={2}
                allowNegative={false}
              />
            )}
          </form.AppField>

          <div className="flex justify-end">
            <form.SubmitButton
              childrenWhileSubmitting="Creating..."
              disabled={form.state.isSubmitting}
            >
              Create
            </form.SubmitButton>
          </div>
        </form>
      </form.AppForm>
    </AdminPageContainer>
  );
}
